import React from 'react';
import { SubItem } from '../components/ExpandableTableRow';

export interface TableRowData {
  type: 'simple' | 'expandable' | 'link';
  label: string;
  amount?: string;
  actionCell?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  amountCellStyle?: React.CSSProperties;
  subItems?: SubItem[];
  onClick?: () => void;
}

export const createTableRowData = (
  accountStatementData: any,
  modalHandlers: any
): TableRowData[] => {
  if (!accountStatementData?.transaction_data) return [];

  const { transaction_data } = accountStatementData;

  return [
    // Previous Balance
    {
      type: 'simple',
      label: 'Previous Balance',
      amount: transaction_data.previous_balance,
    },

    // Price paid by Customer
    {
      type: 'expandable',
      label: 'Price paid by Customer',
      actionCell: (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            modalHandlers.handleShowTotalPricePaidModal();
          }}
          style={{ textDecoration: "underline", cursor: "pointer" }}
        >
          {transaction_data.total_amount_calculated}
        </a>
      ),
      subItems: [
        {
          label: 'Product Price',
          amount: transaction_data.price_paid_by_customer.product_price,
          onClick: modalHandlers.handleShowPricePaidModal,
        },
        {
          label: 'Delivery Fee paid by customer',
          amount: transaction_data.price_paid_by_customer.delivery_fees_paid_by_customer,
          onClick: modalHandlers.handleShowDeliveryFeeModal,
        },
      ],
    },

    // Sale under Return Window
    {
      type: 'expandable',
      label: 'Sale under Return Window',
      amount: transaction_data.sale_under_return_win_total,
      subItems: [
        {
          label: 'Product Price - Under Return Window',
          amount: transaction_data.sale_under_return_window.product_price,
          onClick: modalHandlers.handleShowProductPriceUnderReturnWindowModal,
        },
        {
          label: 'Delivery Fee - Under Return Window',
          amount: transaction_data.sale_under_return_window.return_win_delivery_charges,
          onClick: modalHandlers.handleShowDeliveryFeeUnderReturnWindowModal,
        },
      ],
    },

    // Sale under Processing
    {
      type: 'expandable',
      label: 'Sale under Processing',
      amount: transaction_data.sale_under_processing_total,
      subItems: [
        {
          label: 'Product Price - Return / Refund under processing',
          amount: transaction_data.sale_under_processing.product_price,
          onClick: modalHandlers.handleShowProductPriceReturnRefundUnderProcessinModal,
        },
        {
          label: 'Delivery Fee - Return / Refund under processing',
          amount: transaction_data.sale_under_processing.sale_under_process_delivery_charges,
          onClick: modalHandlers.handleShowDeliveryFeeReturnRefundUnderProcessinModal,
        },
      ],
    },

    // Any other approved claim by seller
    {
      type: 'expandable',
      label: 'Any other approved claim by seller',
      amount: '0',
      subItems: [
        {
          label: 'Product damaged in transit',
          amount: '0',
          isStatic: true,
        },
      ],
    },

    // Sub Total Price Paid by customer
    {
      type: 'simple',
      label: 'Sub Total Price Paid by customer',
      amount: transaction_data.sub_total_price_paid_by_customer,
    },

    // Platform Fees (Including Tax) - Note: This is index 6, not 5
    {
      type: 'expandable',
      label: 'Platform Fees (Including Tax)',
      amount: transaction_data.success_total_amount,
      amountCellStyle: { backgroundColor: "#f7f7f7" },
      subItems: [
        {
          label: 'Platform Commission',
          amount: transaction_data.order_success_data.platform_category_margine,
          onClick: modalHandlers.handleShowPlatformCommissionModal,
        },
        {
          label: 'Transaction',
          amount: transaction_data.order_success_data.transaction_fees,
          onClick: modalHandlers.handleShowTransactionModal,
        },
        {
          label: 'Other Charges 1',
          amount: transaction_data.order_success_data.handling_charges,
          onClick: modalHandlers.handleShowOtherFeeOneModal,
        },
        {
          label: 'Other Charges 2',
          amount: transaction_data.order_success_data.other_fees,
          onClick: modalHandlers.handleShowOtherFeeTwoModal,
        },
        {
          label: 'Delivery Fee',
          amount: transaction_data.order_success_data.delivery_charges_paid,
          onClick: modalHandlers.handleShowPlatformDeliveryFeeModal,
        },
        {
          label: 'Monthly Subscription',
          amount: transaction_data.order_success_data.monthly_subscription,
          isStatic: true,
        },
      ],
    },

    // Returns
    {
      type: 'expandable',
      label: 'Returns',
      amount: transaction_data.return_total_amount,
      amountCellStyle: { backgroundColor: "#f7f7f7" },
      subItems: [
        {
          label: 'Refunded to Customer',
          amount: transaction_data.order_return_data.product_price,
          onClick: modalHandlers.handleShowRefundedToCustomerModal,
        },
        {
          label: 'Commission refunded on return',
          amount: transaction_data.order_return_data.platform_category_margine,
          onClick: modalHandlers.handleShowCommissionRefundedOnReturnModal,
        },
        {
          label: 'Transaction fee refunded on return',
          amount: transaction_data.order_return_data.transaction_fees,
          onClick: modalHandlers.handleShowTransactionFeeRefundedOnReturnModal,
        },
        {
          label: 'Other Fee 1 refunded on return',
          amount: transaction_data.order_return_data.handling_charges,
          onClick: modalHandlers.handleShowOtherFeeOneRefundedOnReturnModal,
        },
        {
          label: 'Other Fee 2 refunded on return',
          amount: transaction_data.order_return_data.other_fees,
          onClick: modalHandlers.handleShowOtherFeeTwoRefundedOnReturnModal,
        },
      ],
    },

    // Withholding Sale Tax
    {
      type: 'expandable',
      label: 'Withholding Sale Tax',
      amount: transaction_data.with_holding_sale_tax_total,
      subItems: [
        {
          label: 'Withholding Sale Tax',
          amount: transaction_data.with_holding_sale_tax,
          isStatic: true,
        },
      ],
    },

    // Sub Total Payable to Seller
    {
      type: 'simple',
      label: 'Sub Total Payable to Seller',
      amount: transaction_data.sub_total_pay_to_seller,
    },

    // COD Collected By Seller
    {
      type: 'simple',
      label: 'COD Collected By Seller',
      amount: transaction_data.total_cod_collect_by_seller,
    },

    // Receivable by Seller
    {
      type: 'simple',
      label: 'Receivable by Seller',
      amount: transaction_data.received_by_seller,
    },

    // Payout Released
    {
      type: 'simple',
      label: 'Payout Released [Date] [Ref]',
      amount: transaction_data.payout_released,
    },

    // Due
    {
      type: 'simple',
      label: 'Due',
      amount: transaction_data.due,
    },
  ];
};